import { useState } from "react";
import { Link, NavLink, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { api } from "@/lib/api";
import { Package, LayoutDashboard, Settings as SettingsIcon, Key, BookOpen, LogOut, Truck, PlusCircle, MapPin, Upload, Menu, Users, Palette, Boxes, PackagePlus, CalendarClock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from "@/components/ui/sheet";
import { useEffect } from "react";

const navItems = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, testId: "nav-dashboard", roles: ["admin", "partner", "ecommerce"] },
  { to: "/orders", label: "Orders", icon: Package, testId: "nav-orders", roles: ["admin", "partner", "ecommerce"] },
  { to: "/incoming", label: "Pre Book", icon: PackagePlus, testId: "nav-incoming", roles: ["admin", "ecommerce"] },
  { to: "/pickup", label: "Pickup", icon: CalendarClock, testId: "nav-pickup", roles: ["admin", "ecommerce"] },
  { to: "/warehouse", label: "Warehouse", icon: Boxes, testId: "nav-warehouse", roles: ["admin", "ecommerce"] },
  { to: "/orders/import", label: "Bulk Import", icon: Upload, testId: "nav-bulk-import", roles: ["admin", "partner", "ecommerce"] },
  { to: "/track", label: "Track shipment", icon: MapPin, testId: "nav-track", roles: ["admin", "partner", "ecommerce"], external: true },
  { to: "/branding", label: "Branding", icon: Palette, testId: "nav-branding", roles: ["partner", "ecommerce"] },
  { to: "/serviceability", label: "Serviceability", icon: MapPin, testId: "nav-serviceability", roles: ["admin", "partner", "ecommerce"] },
  { to: "/keys", label: "API Keys", icon: Key, testId: "nav-api-keys", roles: ["admin", "partner", "ecommerce"] },
  { to: "/developer-docs", label: "API Docs", icon: BookOpen, testId: "nav-api-docs", roles: ["admin", "partner", "ecommerce"] },
  { to: "/partners", label: "Accounts", icon: Users, testId: "nav-partners", roles: ["admin"] },
  { to: "/settings", label: "Settings", icon: SettingsIcon, testId: "nav-settings", roles: ["admin"] },
];

function SidebarBody({ user, onLogout, onNavigate, brand }) {
  const role = user?.role || "partner";
  const items = navItems.filter((i) => i.roles.includes(role));
  const portalLabel = role === "admin" ? "Control Room" : role === "ecommerce" ? "Importer Portal" : "Partner Portal";
  const brandName = brand?.brand_name || "ShipOMS";
  const accent = brand?.brand_color || "#2563EB";
  const logo = brand?.brand_logo_url;
  return (
    <div className="h-full flex flex-col bg-slate-900 text-white">
      <div className="px-6 py-6 border-b border-slate-800">
        <Link to="/" onClick={onNavigate} className="flex items-center gap-2" data-testid="brand-logo">
          <div className="w-9 h-9 rounded-lg flex items-center justify-center shadow-premium-md overflow-hidden shrink-0" style={{ backgroundColor: accent }}>
            {logo ? <img src={logo} alt={brandName} className="w-full h-full object-contain bg-white" /> : <Truck className="w-5 h-5 text-white" />}
          </div>
          <div className="min-w-0">
            <div className="font-chivo font-extrabold tracking-tight text-lg leading-none truncate" data-testid="brand-name">{brandName}</div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-slate-400 mt-1">{portalLabel}</div>
          </div>
        </Link>
      </div>

      <div className="px-3 py-4">
        {role === "ecommerce" ? (
          <Link to="/warehouse" onClick={onNavigate} data-testid="sidebar-new-shipment">
            <Button className="w-full text-white font-medium gap-2 shadow-premium-md hover:opacity-90 transition-opacity" style={{ backgroundColor: accent }}>
              <Boxes className="w-4 h-4" /> New Shipment
            </Button>
          </Link>
        ) : (
          <Link to="/orders/new" onClick={onNavigate} data-testid="sidebar-new-order">
            <Button className="w-full text-white font-medium gap-2 shadow-premium-md hover:opacity-90 transition-opacity" style={{ backgroundColor: accent }}>
              <PlusCircle className="w-4 h-4" /> New Order
            </Button>
          </Link>
        )}
      </div>

      <nav className="flex-1 px-3 space-y-1 overflow-y-auto hide-scrollbar">
        {items.map((item) => (
          item.external ? (
            <a
              key={item.to}
              href={item.to}
              target="_blank"
              rel="noopener noreferrer"
              onClick={onNavigate}
              data-testid={item.testId}
              className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-150 active:scale-[0.98] text-slate-300 hover:bg-slate-800 hover:text-white"
            >
              <item.icon className="w-4 h-4" />
              {item.label}
            </a>
          ) : (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.to === "/"}
            onClick={onNavigate}
            data-testid={item.testId}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-150 active:scale-[0.98] ${
                isActive
                  ? "bg-blue-600 text-white shadow-premium-md"
                  : "text-slate-300 hover:bg-slate-800 hover:text-white"
              }`
            }
          >
            <item.icon className="w-4 h-4" />
            {item.label}
          </NavLink>
          )
        ))}
      </nav>

      <div className="px-3 py-4 border-t border-slate-800">
        <div className="px-3 py-2 mb-2">
          <div className="text-xs text-slate-400 uppercase tracking-wider">Signed in</div>
          <div className="text-sm font-medium truncate" data-testid="current-user-email">{user?.email}</div>
        </div>
        <Button
          variant="ghost"
          className="w-full justify-start gap-2 text-slate-300 hover:bg-slate-800 hover:text-white"
          onClick={onLogout}
          data-testid="logout-button"
        >
          <LogOut className="w-4 h-4" /> Logout
        </Button>
      </div>
    </div>
  );
}

export default function AppShell({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [brand, setBrand] = useState(null);

  useEffect(() => { setOpen(false); }, [location.pathname]);
  useEffect(() => {
    if (user) api.get("/me/branding").then(({ data }) => setBrand(data)).catch(() => {});
  }, [user]);

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const brandName = brand?.brand_name || "ShipOMS";
  const accent = brand?.brand_color || "#2563EB";
  const logo = brand?.brand_logo_url;

  return (
    <div className="min-h-screen bg-[#F9FAFB]">
      {/* Desktop sidebar */}
      <aside className="hidden lg:flex w-64 fixed inset-y-0 left-0 z-30 shadow-premium-lg">
        <SidebarBody user={user} onLogout={handleLogout} brand={brand} />
      </aside>

      {/* Mobile top bar */}
      <header className="lg:hidden sticky top-0 z-40 h-16 bg-white/85 backdrop-blur-xl border-b border-gray-200 flex items-center justify-between px-4">
        <Link to="/" className="flex items-center gap-2 min-w-0" data-testid="mobile-brand-logo">
          <div className="w-8 h-8 rounded-lg flex items-center justify-center overflow-hidden shrink-0" style={{ backgroundColor: accent }}>
            {logo ? <img src={logo} alt={brandName} className="w-full h-full object-contain bg-white" /> : <Truck className="w-4 h-4 text-white" />}
          </div>
          <span className="font-chivo font-extrabold tracking-tight text-base truncate">{brandName}</span>
        </Link>
        <Sheet open={open} onOpenChange={setOpen}>
          <SheetTrigger asChild>
            <Button variant="outline" size="icon" data-testid="mobile-menu-button" aria-label="Open menu">
              <Menu className="w-5 h-5" />
            </Button>
          </SheetTrigger>
          <SheetContent side="left" className="p-0 w-72 border-r-0 bg-slate-900">
            <SheetTitle className="sr-only">Navigation</SheetTitle>
            <SidebarBody user={user} onLogout={handleLogout} onNavigate={() => setOpen(false)} brand={brand} />
          </SheetContent>
        </Sheet>
      </header>

      <main className="lg:ml-64 min-h-screen">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8 animate-fade-rise">{children}</div>
      </main>
    </div>
  );
}
